import { useLayoutEffect, useRef, type RefObject } from 'react';

/**
 * While what a step of the match sets off plays (cards flying, a score bursting, a capture
 * fading), the page is redrawn on every frame, and every animation running on it must then be
 * worked out again for each frame, the idle ones too. So the board's idle motion (pieces
 * floating, cards swaying, the deck breathing, light drifting: styles.css, `[data-busy]`) waits
 * meanwhile, and carries on where it stopped once things are still again. On a phone this halves
 * the work of a turn. The root gets an attribute, not a class: React never touches it.
 */

/** How long a step keeps the screen busy: its flights, effects and score burst. */
export const BUSY_MS = 1800;

export function useQuietWhileBusy(
  root: RefObject<HTMLElement | null>,
  step: string | number,
): void {
  const last = useRef(step);
  useLayoutEffect(() => {
    if (last.current === step) return;
    last.current = step;
    const el = root.current;
    if (!el) return;
    el.setAttribute('data-busy', '');
    // A later step clears this timer and starts its own: busy until the last one has played.
    const timer = setTimeout(() => el.removeAttribute('data-busy'), BUSY_MS);
    return () => clearTimeout(timer);
  }, [root, step]);
}
